import { LoadingState } from "@repo/app-types/client/client";
import type {
  ProjectUpdate,
  ProjectWithInterviewCount,
} from "@repo/app-types/entities/project";
import { ProjectEmailDigestFrequency } from "@repo/app-types/entities/project";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { isValidEmail } from "@repo/app-types/helpers/utilities";
import { SaveAndCancelButtons } from "@repo/ui/Buttons/saveAndCancelButtons";
import { CreatableComboboxField } from "@repo/ui/combobox/creatableComboboxField";
import { Size } from "@repo/ui/helpers/helpers";
import { Label } from "@repo/ui/Label/label";
import { SimpleRadioGroup } from "@repo/ui/Radio/simpleRadioGroup";
import { SimpleRadioOption } from "@repo/ui/Radio/simpleRadioOption";
import { SimpleSelect } from "@repo/ui/Select/simpleSelect";
import { showToast, ToastVariant } from "@repo/ui/toast/toast";
import { type FC, useMemo, useState } from "react";
import { Can } from "../../../helpers/teammateAuthorizationContext.js";
import { useAppDispatch, useAppSelector } from "../../../hooks/hook.js";
import {
  selectAllTeammates,
  selectTeammatesLoadingState,
} from "../../teammates/teammatesSlice.js";
import { updateProject } from "../projectsSlice.js";
import { SlackProjectNotificationsSection } from "../slackProjectNotificationsSection.js";

interface ProjectNotificationsSettingsSectionProps {
  project: ProjectWithInterviewCount;
  onSaveProject: (changes: ProjectUpdate) => void;
  isTrainingProject: boolean;
}

const RecipientTeammatesOption = {
  All: "all",
  ProjectOwners: "owners",
  SpecifiedOnly: "specific",
} as const;
type RecipientTeammatesOption =
  (typeof RecipientTeammatesOption)[keyof typeof RecipientTeammatesOption];

export const ProjectNotificationsSettingsSection: FC<
  ProjectNotificationsSettingsSectionProps
> = ({ project, onSaveProject, isTrainingProject }) => {
  const dispatch = useAppDispatch();
  const teammates = useAppSelector((state) =>
    selectAllTeammates(state.teammates),
  );
  // Offered in the menu; anything else can still be typed in.
  const teammateEmails = useMemo(
    () => teammates.map((teammate) => teammate.email),
    [teammates],
  );
  const teammatesLoadingStatus = useAppSelector(selectTeammatesLoadingState);

  const [recipientTeammatesOption, setRecipientTeammatesOption] = useState(
    computeRecipientTeammatesOption(project),
  );
  const [otherDigestRecipientEmails, setOtherDigestRecipientEmails] = useState<
    string[]
  >(project.settings.email_digest_recipients);
  const [hasChangedDigestRecipientEmails, setHasChangedDigestRecipientEmails] =
    useState(false);

  const [emailDigestFrequency, setEmailDigestFrequency] =
    useState<ProjectEmailDigestFrequency>(
      project.settings.email_digest_frequency,
    );

  const frequencyOptions = [
    { value: ProjectEmailDigestFrequency.INSTANT, label: "Instant" },
    { value: ProjectEmailDigestFrequency.DAILY, label: "Daily" },
  ];

  return (
    <Can
      I={PermissionActions.UPDATE}
      a={PermissionSubjects.JOB_OPENINGS}
      passThrough={true}
    >
      {(allowed) => (
        <>
          <div>
            <Label size={Size.LARGE}>Notifications</Label>
            <div className="mb-3 flex flex-col space-y-2">
              <Label size={Size.SMALL}>Email</Label>
              <SimpleRadioGroup
                value={recipientTeammatesOption}
                onChange={(value) => {
                  setRecipientTeammatesOption(value);
                }}
              >
                <SimpleRadioOption
                  value={RecipientTeammatesOption.All}
                  isDisabled={!allowed}
                >
                  Send to all teammates in your organization{" "}
                  {teammatesLoadingStatus === LoadingState.LOADED
                    ? `(${teammates.length})`
                    : null}
                </SimpleRadioOption>
                <SimpleRadioOption
                  value={RecipientTeammatesOption.ProjectOwners}
                  isDisabled={
                    !allowed || project.owner_teammate_ids.length === 0
                  }
                >
                  Send to {isTrainingProject ? "project" : "job opening"} owners
                  ({project.owner_teammate_ids.length})
                </SimpleRadioOption>
                <SimpleRadioOption
                  value={RecipientTeammatesOption.SpecifiedOnly}
                  isDisabled={!allowed}
                >
                  Send only to recipients specified below
                </SimpleRadioOption>
              </SimpleRadioGroup>
              <div>
                <div className="flex items-center space-x-4">
                  <div className="w-[400px]">
                    <CreatableComboboxField
                      placeholder="Add an email address"
                      value={otherDigestRecipientEmails}
                      suggestions={teammateEmails}
                      onChange={(emails) => {
                        if (emails.length > 50) {
                          showToast({
                            title:
                              "Limit of 50 recipients reached. Consider adding a distribution list email address instead.",
                            variant: ToastVariant.Error,
                          });
                          return;
                        }
                        setHasChangedDigestRecipientEmails(true);
                        setOtherDigestRecipientEmails(
                          emails.filter((email) => isValidEmail(email)),
                        );
                      }}
                      label={
                        recipientTeammatesOption ===
                        RecipientTeammatesOption.SpecifiedOnly
                          ? "Recipients"
                          : "Additional recipients"
                      }
                      isDisabled={!allowed}
                    />
                  </div>
                  <div className="w-[120px]">
                    <Label size={Size.SMALL} className="mb-2">
                      Frequency
                    </Label>
                    <SimpleSelect
                      options={frequencyOptions}
                      value={emailDigestFrequency}
                      onChange={setEmailDigestFrequency}
                      isDisabled={!allowed}
                    />
                  </div>
                </div>
                {hasChangedDigestRecipientEmails ||
                computeRecipientTeammatesOption(project) !==
                  recipientTeammatesOption ||
                project.settings.email_digest_frequency !==
                  emailDigestFrequency ? (
                  <SaveAndCancelButtons
                    onSave={() => {
                      dispatch(
                        updateProject({
                          projectId: project.id,
                          changes: {
                            settings: {
                              ...project.settings,
                              email_digest_recipients:
                                otherDigestRecipientEmails,
                              send_email_digest_to_all_teammates:
                                recipientTeammatesOption ===
                                RecipientTeammatesOption.All,
                              send_email_digest_to_project_owners:
                                recipientTeammatesOption ===
                                  RecipientTeammatesOption.All ||
                                recipientTeammatesOption ===
                                  RecipientTeammatesOption.ProjectOwners,
                              email_digest_frequency: emailDigestFrequency,
                            },
                          },
                        }),
                      );
                      setHasChangedDigestRecipientEmails(false);
                    }}
                    onCancel={() => {
                      setOtherDigestRecipientEmails(
                        project.settings.email_digest_recipients,
                      );
                      setRecipientTeammatesOption(
                        computeRecipientTeammatesOption(project),
                      );
                      setEmailDigestFrequency(
                        project.settings.email_digest_frequency,
                      );
                      setHasChangedDigestRecipientEmails(false);
                    }}
                  />
                ) : null}
              </div>
              <div className="text-sm text-muted-foreground">
                You can add specific teammates or distribution lists that should
                receive email digests as interviews are completed.
              </div>
            </div>
            {isTrainingProject ? null : (
              <SlackProjectNotificationsSection
                project={project}
                onSave={onSaveProject}
              />
            )}
          </div>
        </>
      )}
    </Can>
  );
};

function computeRecipientTeammatesOption(
  project: ProjectWithInterviewCount,
): string {
  return project.settings.send_email_digest_to_all_teammates
    ? RecipientTeammatesOption.All
    : project.settings.send_email_digest_to_project_owners
      ? RecipientTeammatesOption.ProjectOwners
      : RecipientTeammatesOption.SpecifiedOnly;
}
